import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import BaseLayout from './BaseLayout';
import { buildApiUrl } from '../config/api';
import {
  FORGOT_CONFIRMATION,
  forgotErrorMessage,
  validateForgotForm,
} from './forgotForm';
import '../styles/Login.css';

/**
 * Ask for a password reset link.
 *
 * Reuses Login.css rather than introducing a stylesheet: this is the sign-in
 * card with one field, and a second set of nearly identical rules is how the
 * two screens drift apart.
 */
const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const found = validateForgotForm({ email });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsSubmitting(true);
    setError('');
    try {
      await axios.post(buildApiUrl('/auth/password-reset/request'), {
        email: email.trim(),
      });
      // Shown whatever the server found. It answers identically for a known and
      // an unknown address, and this screen must not undo that by rendering
      // something different in the two cases.
      setSent(true);
    } catch (err) {
      setError(forgotErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseLayout>
      <div className="login">
        <div className="card login-card">
          <header className="login-header">
            <span className="pill">Reset password</span>
            <h1 className="page-title login-title">Forgotten your password?</h1>
            <p className="meta-copy login-subtitle">
              Enter your email and we will send you a link to set a new one.
            </p>
          </header>

          {sent && (
            <div className="status-success" role="status">
              {FORGOT_CONFIRMATION}
            </div>
          )}

          {error && (
            <div className="status-error" role="alert">
              {error}
            </div>
          )}

          {!sent && (
            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <div className="login-field">
                <label htmlFor="forgot-email">Email</label>
                <input
                  id="forgot-email"
                  className="field"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors({});
                    setError('');
                  }}
                  required
                  placeholder="you@example.com"
                  aria-invalid={errors.email ? 'true' : undefined}
                  aria-describedby={errors.email ? 'forgot-email-error' : undefined}
                />
                {errors.email && (
                  <p className="status-error" id="forgot-email-error" role="alert">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="login-actions">
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Send reset link'}
                </button>
                <Link to="/login" className="btn-ghost login-register">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}

          {sent && (
            <div className="login-actions">
              <Link to="/login" className="btn-ghost login-register">
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};

export default ForgotPassword;
